<template>

    <EChart
        :options="chartOptions"
        card
        :header="$t('productsRanking')"
        overlay
        :loading="request.getLoading()"
        :error="request.getError()"
    >

        <template #nav>
            <div :key="spaceId" class="d-flex gap-3 flex-column">
                <div class="d-flex gap-3">
                    <DropdownFilter v-model="currentNumberShown" :items="[10, 20, 50]" :prefix="`${$t('show')}: `" :disabled="loading" />
                    <DropdownFilter v-model="currentSortBy" :items="['summary', 'percentDiff']" :prefix="`${$t('sortBy')}: `" :disabled="loading" />
                    <DropdownFilter v-model="currentChart" :items="['impressions', 'clicks', 'quantity', 'amount']" :prefix="`${$t('type')}: `" :disabled="loading" />
                    <MultiselectAvailableCampaigns v-if="showCampaignFilter" :disabled="loading" @changeTrackBy="campaigns = $event" @loading="campaignsLoading = $event" />
                    <Multiselect
                        v-if="showProductGroupsResults"
                        :initial-value="groups"
                        :fetch-options-function="fetchSpaceGroups"
                        :placeholder="$t('productGroup')"
                        :selected-options-label="$t('selectedGroups')"
                        searchable
                        track-by="id"
                        label="name"
                        select-label=""
                        :disabled="loading"
                        show-checkboxes
                        show-selected
                        multiple
                        preselect-all
                        :close-on-select="false"
                        :clear-on-select="false"
                        class="width-200"
                        @inputTrackBy="groups = $event"
                        @loading="groupsLoading = $event"
                    />
                    <FormCategoryTreeSelect
                        v-if="!showProductGroupsResults"
                        ref="FormCategoryTreeSelect"
                        :value="categories"
                        :disabled="loading"
                        class="width-200"
                        preselect
                        prevent-empty-selection
                        :clearable="false"
                        @change="categories = $event"
                        @loading="categoriesLoading = $event"
                    />
                </div>
                <div class="ml-auto d-flex gap-3 justify-content-end align-items-center">
                    <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="loading">{{ $t('showResultsForProductGroups') }}</b-form-checkbox>
                    <b-form-input v-model="filterSearch" :placeholder="$t('search')" :disabled="loading" class="width-200" />
                </div>
            </div>
        </template>

    </EChart>

</template>



<script>
    import { calcPercentDiff } from '@/utils/number';
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';
    import EChart from '@/components/common/EChart.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';

    export default {
        name: 'ProductRanking',
        components: {
            FormCategoryTreeSelect,
            EChart,
            MultiselectAvailableCampaigns,
            Multiselect,
            DropdownFilter,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                region: process.env.VUE_APP_REGION_MODE,
                requestImpressions: this.$req('space/ImpressionsRanking'),
                requestClicks: this.$req('space/ClicksRanking'),
                requestQuantity: this.$req('space/QuantityRanking'),
                requestAmount: this.$req('space/AmountRanking'),
                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                requestSpaceGroups: this.$req('producerProductGroups/SpaceGroups'),
                currentChart: 'impressions',
                currentNumberShown: 10,
                currentSortBy: 'summary',
                campaigns: [],
                categories: [],
                groups: [],
                filterSearch: '',
                showProductGroupsResults: false,
                campaignsLoading: false,
                categoriesLoading: false,
                groupsLoading: false,
            };
        },
        computed: {
            request() {
                if (this.currentChart === 'impressions') return this.requestImpressions;
                if (this.currentChart === 'clicks') return this.requestClicks;
                if (this.currentChart === 'quantity') return this.requestQuantity;
                if (this.currentChart === 'amount') return this.requestAmount;
                return null;
            },
            data() {
                return this.request.getData() || [];
            },
            chartData() {
                return [...this.data]
                    .map(item => ({ ...item, percentDiff: calcPercentDiff(item.summary, item.summaryComparison) }))
                    .filter(item => item.name.toLowerCase().includes(this.filterSearch.toLowerCase()))
                    .sort((a, b) => b[this.currentSortBy] - a[this.currentSortBy])
                    .slice(0, this.currentNumberShown);
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate, compareStartDate, compareEndDate } = this.timeRange;
                const requestParams = { startDate, endDate, compareStartDate, compareEndDate };
                if (this.campaigns?.length) requestParams.campaigns = this.campaigns.join(',');
                if (!this.showProductGroupsResults && this.categories?.length) requestParams.categories = this.categories.join(',');
                if (this.showProductGroupsResults && this.groups?.length) requestParams.groups = this.groups.join(',');
                return { urlParams, requestParams };
            },
            loadingFilters() {
                return this.campaignsLoading || this.categoriesLoading || this.groupsLoading;
            },
            loading() {
                return this.request.getLoading() || this.loadingFilters;
            },
            chartSeries() {
                return [{ name: this.$t(`${this.currentChart}`), data: this.chartData.map(item => item.summary) }];
            },
            chartOptions() {
                // Przygotuj dwie listy: po lewej nazwy kategorii, po prawej wartości percentDiff
                const names = this.chartData.map(item => item.name);
                const diffs = this.chartData.map(item => item.percentDiff);

                return {
                    xAxis: {
                        type: 'value',
                        max: 'dataMax',
                    },
                    yAxis: [
                        // 1) oś po lewej – nazwy
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                        },
                        // 2) oś po prawej – procentowe zmiany
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                            position: 'right',
                            axisLine: { show: false },
                            axisTick: { show: false },
                            axisLabel: {
                                // definiujemy style rich do kolorowania
                                rich: {
                                    pos: {
                                        color: 'green',
                                        backgroundColor: 'rgba(0,255,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    zero: {
                                        color: '#666',
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    neg: {
                                        color: 'red',
                                        backgroundColor: 'rgba(255,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                },
                                formatter: (value, idx) => {
                                    const diff = diffs[idx];
                                    const pct = this.$format.percent(diff);
                                    if (diff > 0) return `{pos|▲${pct}}`;
                                    if (diff < 0) return `{neg|▼${pct}}`;
                                    return `{zero|${pct}}`;
                                },
                            },
                        },
                    ],
                    series: [
                        {
                            type: 'bar',
                            stack: 'total',
                            // etykiety wewnątrz tylko z wartościami liczbowymi
                            label: {
                                show: true,
                                position: 'insideLeft',
                                formatter: params => params.data.value,
                            },
                            data: this.chartData.map(item => ({ value: item.summary })),
                        },
                    ],
                };
            },
            showCampaignFilter() {
                return this.region !== 'PL' || (this.currentChart !== 'quantity' && this.currentChart !== 'amount');
            },
        },
        watch: {
            spaceId() {
                this.resetFilters();
            },
            apiParams: {
                immediate: true,
                handler(newVal, oldVal) {
                    if (JSON.stringify(newVal || {}) !== JSON.stringify(oldVal || {})) {
                        this.loadData();
                    }
                },
            },
            currentChart: {
                handler: 'loadData',
            },
        },
        methods: {
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id, v: 1 } });
            },
            fetchSpaceGroups() {
                return this.requestSpaceGroups.get({ urlParams: { spaceId: this.spaceId } }).then(data => data.items);
            },
            loadData() {
                if (!this.loadingFilters) {
                    this.resetData();
                    this.fetchData();
                }
            },
            resetData() {
                this.request.abortAll();
                this.request.resetAll();
            },
            fetchData() {
                this.request.get(this.apiParams);
            },
            resetFilters() {
                this.currentChart = 'impressions';
                this.currentNumberShown = 10;
                this.currentSortBy = 'summary';
                this.campaigns = [];
                this.categories = [];
                this.groups = [];
                this.filterSearch = '';
                this.showProductGroupsResults = false;
                this.campaignsLoading = false;
                this.categoriesLoading = false;
                this.groupsLoading = false;
            },
        },
        created() {
            this.parentComponent.$on('resetFilters', this.resetFilters);
        },
        beforeDestroy() {
            this.parentComponent.$off('resetFilters', this.resetFilters);
        },
    };
</script>
